import React from 'react';
import ClipboardPencil from '@uber/base-icons/clipboard-pencil';
import { useStyletron } from 'baseui';
import { SIZE, Tag } from 'baseui/tag';
import { useAtomValue, useSetAtom } from 'jotai';
import { useDrawer } from '../../../../common/multi-level-drawer';
import { useLogger } from '../../../../modernization-common/hooks/use-logger';
import type { QAMetadata } from '../../../../qa-checks/type/common/types';
import type { ImageMetadataTypes, QACheckResult } from '../../../../qa-checks/type/qa-engine';
import { TOOL_CONFIG } from '../../../labelling/components/toolbar/constants';
import { dispatchAtom } from '../../atoms/label-store-dispatch/dispatch';
import { EVENTS } from '../../atoms/label-store-dispatch/events';
import { isVideoLabelingM4EnabledAtom } from '../../atoms/m4-atoms';
import { annotationsAtom } from '../../atoms/side-panel-atoms';
import { useOpenTaskQuestionnaireDrawer } from '../../hooks/use-open-task-questionnaire-drawer';
const getTagOverrides = (color: string, textColor: string) => ({
  Root: {
    style: () => ({
      backgroundColor: color,
      color: textColor,
      borderColor: color
    })
  }
});
type Props = {
  metadata: QAMetadata<ImageMetadataTypes>[];
  ruleResult: QACheckResult<ImageMetadataTypes>;
};
export const RuleEngineActions = ({
  metadata
}: Props) => {
  const [css, theme] = useStyletron();
  const logger = useLogger();
  const isM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const {
    close
  } = useDrawer();
  const {
    openTaskQuestionnaireDrawer
  } = useOpenTaskQuestionnaireDrawer();
  const annotations = useAtomValue(annotationsAtom);
  const dispatch = useSetAtom(dispatchAtom);
  return <>
      <span className={css({
      ...theme.typography.LabelXSmall
    })}>Entities part of rule</span>
      <div>
        {metadata.map(meta => {
        switch (meta.type) {
          case 'ANNOTATION':
            {
              const annotation = annotations.find(annotation => annotation.id === meta.id);
              if (!annotation) {
                logger?.error('Invalid annotation passed to rule error renderer');
                return;
              }
              const annotationLabelColor = annotation.annotationLabel.style.strokeColor;
              const annotationIcon = TOOL_CONFIG[annotation.annotationType].Icon;
              return <Tag key={meta.id} startEnhancer={annotationIcon} size={SIZE.small} onClick={() => {
                // Non-M4 edits render through the separate StatefulSideDrawer, so the
                // Remaining Items entry (on the v2 stack) must be dismissed explicitly
                // here. M4 edits open on the same v2 stack, so closing here would drop
                // Remaining Items instead of leaving it underneath.
                if (!isM4Enabled) {
                  close();
                }
                dispatch({
                  type: EVENTS.SELECT_ANNOTATION,
                  annotationId: annotation.id
                });
                dispatch({
                  type: EVENTS.OPEN_ANNOTATION_DETAILS_PANEL
                });
              }} overrides={getTagOverrides(annotationLabelColor, theme.colors.white)} closeable={false}>
                  {annotation.id}
                </Tag>;
            }
          case 'TASK_QUESTIONNAIRE':
            {
              return <Tag key={meta.id} startEnhancer={() => <ClipboardPencil />} size={SIZE.small} onClick={() => {
                // Non-M4 opens the task questionnaire on the separate v1
                // StatefulSideDrawer, so the Remaining Items entry (on the v2 stack)
                // must be dismissed explicitly here. M4 opens on the same v2 stack,
                // so closing here would drop Remaining Items instead of leaving it
                // underneath.
                if (!isM4Enabled) {
                  close();
                }
                openTaskQuestionnaireDrawer();
              }} overrides={getTagOverrides(theme.colors.gray100Dark, theme.colors.white)} closeable={false}>
                  Task questionnaire
                </Tag>;
            }
        }
      })}
      </div>
    </>;
};